import { useSubmit } from "@hyper-fetch/react";
import { FileText, Receipt } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { DialogShell } from "@/components/layouts/shells/dialog-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { m } from "@/paraglide/messages.js";

// Module level, so a test's `setMock` reaches the same request instances the component sends.
const requestInvoiceRequest = sdk.me.payments.payments.$paymentId.invoiceRequest.$post;
const invoiceRequest = sdk.me.payments.payments.$paymentId.invoice.$get;

type InvoiceState = "none" | "requested" | "ready";

function RequestDialog({ paymentId, title, onClose }: { paymentId: string; title: string; onClose: () => void }) {
  const [buyerName, setBuyerName] = useState("");
  const [buyerTaxId, setBuyerTaxId] = useState("");
  const [buyerAddress, setBuyerAddress] = useState("");
  const [tried, setTried] = useState(false);
  const send = useSubmit(requestInvoiceRequest);
  const nameMissing = !buyerName.trim();

  const submit = async () => {
    setTried(true);
    if (nameMissing) return;
    const { data, error } = await send.submit({
      params: { paymentId },
      payload: { buyerName: buyerName.trim(), buyerTaxId: buyerTaxId.trim(), buyerAddress: buyerAddress.trim(), buyerEmail: "" },
    });
    if (error) {
      toast.error(error.message || m.invoice_request_failed());
      return;
    }
    client.cache.invalidate(/\/me\/payments\/orders/);
    // With the business's details complete the app issues it on the spot (ADR 0034).
    toast.success(data?.status === "issued" ? m.invoice_request_ready() : m.invoice_request_sent());
    onClose();
  };

  return (
    <DialogShell
      open
      onOpenChange={(open) => !open && !send.submitting && onClose()}
      icon={Receipt}
      title={m.invoice_request_title()}
      description={title}
      className="sm:max-w-md"
      footer={
        <div className="flex justify-end">
          <Button type="button" onClick={() => void submit()} disabled={send.submitting}>
            {send.submitting ? <Spinner /> : null}
            {m.invoice_request_send()}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <p className="text-sm text-muted-foreground">{m.invoice_request_explainer()}</p>
        <Field className="gap-2">
          <FieldLabel htmlFor="invoice-buyer-name">{m.invoice_request_buyer_name()}</FieldLabel>
          <Input
            id="invoice-buyer-name"
            value={buyerName}
            maxLength={255}
            aria-invalid={(tried && nameMissing) || undefined}
            onChange={(event) => setBuyerName(event.target.value)}
          />
          {tried && nameMissing ? <p className="text-xs text-destructive">{m.invoice_request_buyer_name_error()}</p> : null}
        </Field>
        <Field className="gap-2">
          <FieldLabel htmlFor="invoice-buyer-tax-id">{m.invoice_request_tax_id()}</FieldLabel>
          <Input id="invoice-buyer-tax-id" value={buyerTaxId} maxLength={64} onChange={(event) => setBuyerTaxId(event.target.value)} />
          <FieldDescription className="text-xs">{m.invoice_request_tax_id_hint()}</FieldDescription>
        </Field>
        <Field className="gap-2">
          <FieldLabel htmlFor="invoice-buyer-address">{m.invoice_request_address()}</FieldLabel>
          <Input id="invoice-buyer-address" value={buyerAddress} maxLength={500} onChange={(event) => setBuyerAddress(event.target.value)} />
        </Field>
      </div>
    </DialogShell>
  );
}

/**
 * The invoice side of one payment: ask for an invoice, wait for it, or download it. The app usually
 * issues it the moment it is asked for (ADR 0034); until there is one, the confirmation is the proof.
 */
export function InvoiceAction({
  paymentId,
  title,
  state,
  onOpen = (url) => window.open(url, "_blank", "noopener"),
}: {
  paymentId: string;
  title: string;
  state: InvoiceState;
  onOpen?: (url: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const download = useSubmit(invoiceRequest);

  if (state === "requested") return <Badge variant="secondary">{m.invoice_request_pending()}</Badge>;

  if (state === "ready") {
    const open = async () => {
      const { data, error } = await download.submit({ params: { paymentId } });
      if (error || !data) {
        toast.error(error?.message || m.invoice_download_failed());
        return;
      }
      onOpen(data.url);
    };
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => void open()} disabled={download.submitting}>
        {download.submitting ? <Spinner /> : <FileText aria-hidden />}
        {m.invoice_download()}
      </Button>
    );
  }

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={() => setAsking(true)}>
        {m.invoice_request_action()}
      </Button>
      {asking ? <RequestDialog paymentId={paymentId} title={title} onClose={() => setAsking(false)} /> : null}
    </>
  );
}
